// ============================================================================
// BP-09-04: Support Groups API Client
// BrightPath Community - Support Groups API
// ============================================================================

import apiClient from '@/core/lib/api-client';
import {
  SupportGroup,
  SupportGroupListItem,
  SupportGroupsResponse,
  SupportGroupDetailResponse,
  SupportGroupMember,
  SupportGroupMeeting,
  UserGroupsResponse,
  GroupStatsResponse,
  SupportGroupSearchParams,
  CreateSupportGroupInput,
  UpdateSupportGroupInput,
  CreateMeetingInput,
  UpdateMemberInput,
} from '../types';

const GROUPS_BASE = '/support-groups';

// =============================================================================
// Support Groups API
// =============================================================================

export const supportGroupApi = {
  /**
   * Get list of support groups
   */
  async getGroups(params?: SupportGroupSearchParams): Promise<SupportGroupsResponse> {
    const response = await apiClient.get<SupportGroupsResponse>(GROUPS_BASE, { params });
    return response.data;
  },

  /**
   * Get a single group by ID
   */
  async getGroup(groupId: string): Promise<SupportGroupDetailResponse> {
    const response = await apiClient.get<SupportGroupDetailResponse>(
      `${GROUPS_BASE}/${groupId}`
    );
    return response.data;
  },

  /**
   * Get support group statistics
   */
  async getGroupStats(): Promise<GroupStatsResponse> {
    const response = await apiClient.get<GroupStatsResponse>(`${GROUPS_BASE}/stats`);
    return response.data;
  },

  /**
   * Create a new support group
   */
  async createGroup(data: CreateSupportGroupInput): Promise<SupportGroup> {
    const response = await apiClient.post<SupportGroup>(GROUPS_BASE, data);
    return response.data;
  },

  /**
   * Update a support group
   */
  async updateGroup(groupId: string, data: UpdateSupportGroupInput): Promise<SupportGroup> {
    const response = await apiClient.patch<SupportGroup>(
      `${GROUPS_BASE}/${groupId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a support group
   */
  async deleteGroup(groupId: string): Promise<void> {
    await apiClient.delete(`${GROUPS_BASE}/${groupId}`);
  },

  // ===========================================================================
  // Membership
  // ===========================================================================

  /**
   * Join a support group
   */
  async joinGroup(groupId: string): Promise<SupportGroupMember> {
    const response = await apiClient.post<SupportGroupMember>(
      `${GROUPS_BASE}/${groupId}/join`
    );
    return response.data;
  },

  /**
   * Leave a support group
   */
  async leaveGroup(groupId: string): Promise<void> {
    await apiClient.delete(`${GROUPS_BASE}/${groupId}/leave`);
  },

  /**
   * Get group members
   */
  async getGroupMembers(groupId: string): Promise<SupportGroupMember[]> {
    const response = await apiClient.get<SupportGroupMember[]>(
      `${GROUPS_BASE}/${groupId}/members`
    );
    return response.data;
  },

  /**
   * Update a member (for facilitators)
   */
  async updateMember(
    groupId: string,
    userId: string,
    data: UpdateMemberInput
  ): Promise<SupportGroupMember> {
    const response = await apiClient.patch<SupportGroupMember>(
      `${GROUPS_BASE}/${groupId}/members/${userId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove a member (for facilitators)
   */
  async removeMember(groupId: string, userId: string): Promise<void> {
    await apiClient.delete(`${GROUPS_BASE}/${groupId}/members/${userId}`);
  },

  /**
   * Approve a pending member (for private groups)
   */
  async approveMember(groupId: string, userId: string): Promise<SupportGroupMember> {
    return this.updateMember(groupId, userId, { status: 'active' });
  },

  // ===========================================================================
  // User's Groups
  // ===========================================================================

  /**
   * Get current user's groups
   */
  async getMyGroups(): Promise<UserGroupsResponse> {
    const response = await apiClient.get<UserGroupsResponse>('/users/me/support-groups');
    return response.data;
  },

  // ===========================================================================
  // Meetings
  // ===========================================================================

  /**
   * Get group meetings
   */
  async getGroupMeetings(groupId: string): Promise<SupportGroupMeeting[]> {
    const response = await apiClient.get<SupportGroupMeeting[]>(
      `${GROUPS_BASE}/${groupId}/meetings`
    );
    return response.data;
  },

  /**
   * Get upcoming meetings across all user's groups
   */
  async getUpcomingMeetings(): Promise<SupportGroupMeeting[]> {
    const response = await apiClient.get<SupportGroupMeeting[]>(
      '/users/me/support-groups/upcoming-meetings'
    );
    return response.data;
  },

  /**
   * Create a meeting (for facilitators)
   */
  async createMeeting(
    groupId: string,
    data: CreateMeetingInput
  ): Promise<SupportGroupMeeting> {
    const response = await apiClient.post<SupportGroupMeeting>(
      `${GROUPS_BASE}/${groupId}/meetings`,
      data
    );
    return response.data;
  },

  /**
   * Update a meeting
   */
  async updateMeeting(
    meetingId: string,
    data: Partial<CreateMeetingInput>
  ): Promise<SupportGroupMeeting> {
    const response = await apiClient.patch<SupportGroupMeeting>(
      `/support-group-meetings/${meetingId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a meeting
   */
  async deleteMeeting(meetingId: string): Promise<void> {
    await apiClient.delete(`/support-group-meetings/${meetingId}`);
  },

  /**
   * Record attendance for a meeting
   */
  async recordAttendance(meetingId: string): Promise<void> {
    await apiClient.post(`/support-group-meetings/${meetingId}/attend`);
  },
};

export default supportGroupApi;
